import React from "react"
import { Link } from "react-router-dom"
import {
  FaBoxes,
  FaClipboardList,
  FaWarehouse,
  FaCube,
  FaRulerCombined,
  FaTags,
  FaDolly,
  FaFileInvoiceDollar,
  FaClipboardCheck,
  FaRecycle,
  FaBalanceScale,
  FaTruck,
  FaStream,
  FaUserTie,
} from "react-icons/fa"
import { useAuth } from "../../provider/auth.provider"

/**
 * The Material Management hub.
 *
 * Laid out along the life of a piece of material rather than alphabetically,
 * because the order is the thing people get wrong: a request is not a
 * purchase, an issue is not a consumption, and only two of these steps
 * actually change what the company owns.
 */

interface Card {
  label: string
  path: string
  icon: React.ComponentType<any>
  hint: string
  resource: string
  action: string
}

const FLOW: Card[] = [
  {
    label: "Material Requests (MRN)",
    path: "/materials/requests",
    icon: FaClipboardList,
    hint: "Ask for material to be procured - warehouse purchase or staff purchase. Moves no stock.",
    resource: "MaterialRequest",
    action: "view_all",
  },
  {
    label: "Arrangement & Payment (MAP)",
    path: "/materials/arrangements",
    icon: FaFileInvoiceDollar,
    hint: "Supplier, rates, invoice, payment - and the goods receipt that puts material into stock.",
    resource: "MaterialArrangement",
    action: "view_all",
  },
  {
    label: "Issue Notes (MIN)",
    path: "/materials/issues",
    icon: FaDolly,
    hint: "Move material between holders: to staff, to site, back to a warehouse. Released, not consumed.",
    resource: "MaterialIssue",
    action: "view_all",
  },
  {
    label: "Consumption Reports (MCR)",
    path: "/materials/consumptions",
    icon: FaClipboardCheck,
    hint: "What was installed. Two approvals, and the second one consumes the stock.",
    resource: "MaterialConsumption",
    action: "view_all",
  },
  {
    label: "Scrap, Sale, Loss & Adjustment",
    path: "/materials/scraps",
    icon: FaRecycle,
    hint: "Everything that leaves the books without being installed.",
    resource: "MaterialScrap",
    action: "view_all",
  },
]

const POSITION: Card[] = [
  {
    label: "Stock & Ledger",
    path: "/materials/stock",
    icon: FaStream,
    hint: "Where the material is right now, and every movement that put it there.",
    resource: "MaterialInventory",
    action: "view_balances",
  },
  {
    label: "Reconciliation",
    path: "/materials/reco",
    icon: FaBalanceScale,
    hint: "Group heads plus the billable, consumption, warehouse, staff and site reconciliations.",
    resource: "MaterialReco",
    action: "view",
  },
  {
    label: "Opening Balances",
    path: "/materials/opening-balances",
    icon: FaBoxes,
    hint: "Load the stock you already hold on paper. Imported, reviewed, then approved onto the books.",
    resource: "MaterialOpeningBalance",
    action: "view_all",
  },
]

const MASTERS: Card[] = [
  { label: "Warehouses", path: "/materials/warehouses", icon: FaWarehouse, hint: "Where company stock is held", resource: "MaterialWarehouse", action: "view_all" },
  { label: "Items", path: "/materials/items", icon: FaCube, hint: "Item master, billable flag and stock thresholds", resource: "MaterialItem", action: "view_all" },
  { label: "Suppliers", path: "/materials/suppliers", icon: FaUserTie, hint: "Who the company buys material from, with payment details", resource: "MaterialSupplier", action: "view_all" },
  { label: "Transporters", path: "/materials/transporters", icon: FaTruck, hint: "Who moves consignments", resource: "MaterialTransporter", action: "view_all" },
  { label: "Classification Categories", path: "/materials/classification-categories", icon: FaTags, hint: "Top-level item grouping", resource: "MaterialClassificationCategory", action: "view_all" },
  { label: "Sub Categories", path: "/materials/classification-subcategories", icon: FaClipboardList, hint: "Nested classification", resource: "MaterialClassificationSubCategory", action: "view_all" },
  { label: "Units", path: "/materials/units", icon: FaRulerCombined, hint: "Units of measure", resource: "MaterialUnit", action: "view_all" },
  { label: "Cost Categories", path: "/materials/cost-categories", icon: FaBoxes, hint: "Cost buckets for valuation", resource: "MaterialCostCategory", action: "view_all" },
]

export default function MaterialManagementPage() {
  const { hasPermission } = useAuth()
  const visible = (cards: Card[]) => cards.filter((c) => hasPermission(c.resource, c.action))

  const flow = visible(FLOW)
  const position = visible(POSITION)
  const masters = visible(MASTERS)

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Material Management</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Material moves in four ways: it comes <strong>in</strong> (purchase, opening stock), it is{" "}
          <strong>released</strong> to staff or a site without being used, it comes <strong>back</strong>, and it goes{" "}
          <strong>out</strong> for good (installed, sold, discarded, lost). Only the first and last change what the
          company owns - the middle two just move it around, and that is what this module keeps track of.
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Everything here is scoped to the projects you are assigned to.
        </p>
      </div>

      {flow.length ? <CardGroup title="The flow of material" cards={flow} /> : null}
      {position.length ? <CardGroup title="Where things stand" cards={position} /> : null}
      {masters.length ? <CardGroup title="Masters" cards={masters} /> : null}

      {!flow.length && !position.length && !masters.length ? (
        <p className="text-sm text-gray-500">You do not have access to any Material Management screen yet.</p>
      ) : null}
    </div>
  )
}

const CardGroup: React.FC<{ title: string; cards: Card[] }> = ({ title, cards }) => (
  <div>
    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {cards.map((card) => (
        <Link
          key={card.path}
          to={card.path}
          className="rounded-xl border bg-white p-5 hover:border-blue-400 hover:shadow-sm transition-all"
        >
          <card.icon className="text-2xl text-slate-700" />
          <div className="mt-4">
            <h3 className="font-semibold text-gray-900">{card.label}</h3>
            <p className="text-sm text-gray-500 mt-1">{card.hint}</p>
          </div>
        </Link>
      ))}
    </div>
  </div>
)
